import React, { ReactNode } from 'react'
import { Box, Tooltip, TooltipProps, Typography, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

type NewProductFeaturesBadgeProps = {
  color?: string
  icon?: React.ReactNode
  iconAlt?: string
  text: string
  textColor?: string
  tooltipEnterDelay?: number
  tooltipPlacement?: TooltipProps['placement']
  tooltipTitle?: ReactNode
}

const useStyles = makeStyles<{
  bgColor: string
  textColor: string
}>()((theme, { bgColor, textColor }) => {
  const borderRadius = theme.spacing(1.5)
  const size = theme.spacing(4)

  return {
    iconContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      zIndex: 1,
    },
    iconImg: {
      height: size,
      width: size,
    },
    root: {
      alignItems: 'center',
      backgroundColor: bgColor,
      borderRadius,
      display: 'flex',
      gap: theme.spacing(1),
      height: theme.spacing(6),
      padding: theme.spacing(0.5, 2.5),
      position: 'relative',
      '&::before': {
        background: bgColor,
        borderRadius,
        bottom: 0,
        content: '""',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        transform: 'skewX(15deg)',
        transformOrigin: 'top left',
        zIndex: -1,
      },
      width: 'fit-content',
    },
    text: {
      color: textColor,
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 600,
      lineHeight: size,
      whiteSpace: 'nowrap',
      zIndex: 1,
    },
  }
})

export const NewProductFeaturesBadge: React.FC<
  NewProductFeaturesBadgeProps
> = ({
  color,
  icon,
  iconAlt,
  text,
  textColor,
  tooltipEnterDelay = 1000,
  tooltipPlacement = 'bottom',
  tooltipTitle,
}) => {
  const theme = useTheme()

  const defaultBgColor = color || theme.palette.grey['500']
  const defaultTextColor = textColor || theme.palette.common.white

  const { classes } = useStyles({
    bgColor: defaultBgColor,
    textColor: defaultTextColor,
  })

  return (
    <Tooltip
      title={tooltipTitle}
      placement={tooltipPlacement}
      enterDelay={tooltipEnterDelay}
      data-testid="new-product-feature-badge"
    >
      <Box className={classes.root}>
        {icon &&
          (typeof icon === 'string' ? (
            <img className={classes.iconImg} src={icon} alt={iconAlt} />
          ) : (
            icon
          ))}
        <Typography className={classes.text}>{text}</Typography>
      </Box>
    </Tooltip>
  )
}

export default NewProductFeaturesBadge
